import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Field } from "@/pages/Leads";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Pencil, Merge, Search } from "lucide-react";

const FIELDS = [
  { key: "source", label: "Source" },
  { key: "sub_source", label: "Sub Source" },
  { key: "stage", label: "Stage" },
  { key: "status", label: "Status" },
  { key: "sub_status", label: "Sub Status" },
  { key: "category", label: "Category" },
  { key: "concern", label: "Concern" },
  { key: "service", label: "Service" },
  { key: "location", label: "Location" },
  { key: "followup_type", label: "Follow-up Type" },
  { key: "followup_priority", label: "Follow-up Priority" },
  { key: "followup_outcome", label: "Follow-up Outcome" },
];

/* ── Preset Tailwind badge color combos available for stage configuration ── */
export const STAGE_COLOR_OPTIONS = [
  { value: "bg-indigo-100 text-indigo-800", label: "Indigo" },
  { value: "bg-red-100 text-red-800", label: "Red" },
  { value: "bg-amber-100 text-amber-800", label: "Amber" },
  { value: "bg-blue-100 text-blue-800", label: "Blue" },
  { value: "bg-purple-100 text-purple-800", label: "Purple" },
  { value: "bg-sky-100 text-sky-800", label: "Sky" },
  { value: "bg-orange-100 text-orange-800", label: "Orange" },
  { value: "bg-emerald-100 text-emerald-800", label: "Emerald" },
  { value: "bg-green-100 text-green-800", label: "Green" },
  { value: "bg-yellow-100 text-yellow-800", label: "Yellow" },
  { value: "bg-gray-200 text-gray-700", label: "Gray" },
  { value: "bg-gray-100 text-gray-500", label: "Light Gray" },
];

export default function MasterData() {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { field, item } or { field } for new
  const [merging, setMerging] = useState(null); // { field } when merge dialog is open

  const load = useCallback(() => {
    setLoading(true);
    api.get("/master-data/all").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleActive = async (item) => {
    try {
      await api.put(`/master-data/${item.id}`, { active: !item.active });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (item) => {
    if (!window.confirm(`Remove "${item.label}"?`)) return;
    try {
      const { data: res } = await api.delete(`/master-data/${item.id}`);
      toast.success(res.message || "Removed");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5">
      <Tabs defaultValue="source">
        <TabsList className="bg-cream flex-wrap h-auto gap-1 p-1">
          {FIELDS.map((f) => (
            <TabsTrigger key={f.key} value={f.key} data-testid={`master-data-tab-${f.key}`}>{f.label}</TabsTrigger>
          ))}
        </TabsList>
        {FIELDS.map((f) => (
          <TabsContent key={f.key} value={f.key} className="mt-4">
            <div className="overflow-hidden rounded-xl border border-navy/10 bg-white shadow-sm">
              <div className="flex items-center justify-between border-b bg-cream/50 px-4 py-3">
                <h3 className="font-display font-semibold text-navy">{f.label} Options</h3>
                <div className="flex gap-2">
                  {(data[f.key] || []).length > 1 && (
                    <Button size="sm" variant="outline" onClick={() => setMerging({ field: f.key })} className="border-navy/20 text-navy hover:bg-navy/5" data-testid={`merge-${f.key}-option`}>
                      <Merge className="mr-1.5 h-4 w-4" /> Merge Duplicates
                    </Button>
                  )}
                  <Button size="sm" onClick={() => setEditing({ field: f.key })} className="bg-navy text-white hover:bg-navy-hover" data-testid={`add-${f.key}-option`}>
                    <Plus className="mr-1.5 h-4 w-4" /> Add Option
                  </Button>
                </div>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="px-4 py-2">Value</th>
                    <th className="px-4 py-2">Label</th>
                    {f.key === "sub_source" && <th className="px-4 py-2">Parent Source</th>}
                    {f.key === "stage" && <th className="px-4 py-2">Color</th>}
                    {f.key === "stage" && <th className="px-4 py-2">Rotting Days</th>}
                    <th className="px-4 py-2">Active</th>
                    <th className="px-4 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={10} className="px-4 py-8 text-center text-muted-foreground">Loading…</td></tr>
                  ) : (data[f.key] || []).length === 0 ? (
                    <tr><td colSpan={10} className="px-4 py-8 text-center text-muted-foreground">No options yet</td></tr>
                  ) : (
                    (data[f.key] || []).map((item) => (
                      <tr key={item.id} className="border-b last:border-0 hover:bg-nude/30" data-testid={`master-data-${f.key}-${item.value}`}>
                        <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{item.value}</td>
                        <td className="px-4 py-2 font-medium text-ink">{item.label}</td>
                        {f.key === "sub_source" && (
                          <td className="px-4 py-2 text-muted-foreground">{item.parent || "—"}</td>
                        )}
                        {f.key === "stage" && (
                          <td className="px-4 py-2">
                            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${item.color || "bg-gray-100 text-gray-700"}`}>
                              {item.label}
                            </span>
                          </td>
                        )}
                        {f.key === "stage" && (
                          <td className="px-4 py-2 text-muted-foreground">{item.rotting_days ?? "—"}</td>
                        )}
                        <td className="px-4 py-2">
                          <Switch checked={item.active !== false} onCheckedChange={() => toggleActive(item)} data-testid={`toggle-${f.key}-${item.value}`} />
                        </td>
                        <td className="px-4 py-2 text-right">
                          <Button size="sm" variant="outline" onClick={() => setEditing({ field: f.key, item })} className="mr-2 border-navy/20 text-navy hover:bg-navy/5" data-testid={`edit-${f.key}-${item.value}`}>
                            <Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => remove(item)} className="border-red-200 text-red-600 hover:bg-red-50" data-testid={`delete-${f.key}-${item.value}`}>
                            <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Delete
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </TabsContent>
        ))}
      </Tabs>

      <EditDialog editing={editing} onClose={() => setEditing(null)} onDone={load} sources={(data.source || []).map(s => s.value)} />
      <MergeDialog merging={merging} items={merging ? (data[merging.field] || []) : []} onClose={() => setMerging(null)} onDone={load} />
    </div>
  );
}

function EditDialog({ editing, onClose, onDone, sources = [] }) {
  const isNew = editing && !editing.item;
  const isStage = editing?.field === "stage";
  const isSubSource = editing?.field === "sub_source";
  const [value, setValue] = useState("");
  const [label, setLabel] = useState("");
  const [parent, setParent] = useState("");
  const [color, setColor] = useState(STAGE_COLOR_OPTIONS[0].value);
  const [rottingDays, setRottingDays] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editing?.item) {
      setValue(editing.item.value);
      setLabel(editing.item.label);
      setParent(editing.item.parent || "");
      setColor(editing.item.color || STAGE_COLOR_OPTIONS[0].value);
      setRottingDays(editing.item.rotting_days ?? "");
    } else {
      setValue("");
      setLabel("");
      setParent("");
      setColor(STAGE_COLOR_OPTIONS[0].value);
      setRottingDays("");
    }
  }, [editing]);

  if (!editing) return null;

  const submit = async () => {
    if (isNew && !value.trim()) { toast.error("Value is required"); return; }
    if (isSubSource && !parent) { toast.error("Parent source is required"); return; }
    setSaving(true);
    try {
      if (isNew) {
        const payload = { field: editing.field, value: value.trim(), label: label.trim() || value.trim() };
        if (isSubSource) payload.parent = parent;
        await api.post("/master-data", payload);
        toast.success("Option added");
      } else {
        const payload = { label: label.trim() || editing.item.value };
        if (isStage) {
          payload.color = color;
          payload.rotting_days = rottingDays === "" ? 0 : Number(rottingDays);
        }
        await api.put(`/master-data/${editing.item.id}`, payload);
        toast.success("Option updated");
      }
      onClose();
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!editing} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader><DialogTitle className="font-display text-navy">{isNew ? "Add Option" : "Edit Option"}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          {isNew && (
            <Field label="Value *">
              <Input value={value} onChange={(e) => setValue(e.target.value)} data-testid="master-data-value-input" />
            </Field>
          )}
          <Field label="Label">
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder={value} data-testid="master-data-label-input" />
          </Field>
          {isSubSource && (
            <Field label="Parent Source *">
              <Select value={parent} onValueChange={setParent}>
                <SelectTrigger data-testid="master-data-parent-select"><SelectValue placeholder="Select source" /></SelectTrigger>
                <SelectContent>
                  {sources.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          )}
          {isStage && !isNew && (
            <>
              <Field label="Badge Color">
                <Select value={color} onValueChange={setColor}>
                  <SelectTrigger data-testid="master-data-color-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STAGE_COLOR_OPTIONS.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${c.value}`}>{c.label}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Rotting Days">
                <Input
                  type="number"
                  min="0"
                  value={rottingDays}
                  onChange={(e) => setRottingDays(e.target.value)}
                  placeholder="Days before flagged as rotting"
                  data-testid="master-data-rotting-days-input"
                />
              </Field>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-navy text-white hover:bg-navy-hover" data-testid="save-master-data-btn">{saving ? "Saving…" : "Save"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MergeDialog({ merging, items, onClose, onDone }) {
  const [selected, setSelected] = useState([]);
  const [targetId, setTargetId] = useState("");
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    setSelected([]);
    setTargetId("");
    setQuery("");
  }, [merging]);

  if (!merging) return null;

  const fieldLabel = FIELDS.find((f) => f.key === merging.field)?.label || merging.field;

  const q = query.trim().toLowerCase();
  const visibleItems = q
    ? items.filter((i) => (i.label || "").toLowerCase().includes(q) || (i.value || "").toLowerCase().includes(q))
    : items;

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      if (targetId && !next.includes(targetId)) setTargetId("");
      return next;
    });
  };

  // Select All acts on the currently visible (filtered) items so it doubles as
  // "select all matches" when a search is active.
  const allVisibleSelected = visibleItems.length > 0 && visibleItems.every((i) => selected.includes(i.id));
  const selectAll = () => {
    if (allVisibleSelected) {
      const visibleIds = new Set(visibleItems.map((i) => i.id));
      setSelected((prev) => prev.filter((id) => !visibleIds.has(id)));
      if (targetId && visibleIds.has(targetId)) setTargetId("");
    } else {
      setSelected((prev) => Array.from(new Set([...prev, ...visibleItems.map((i) => i.id)])));
    }
  };

  // Group items by lowercase value to highlight duplicates
  const valueCounts = {};
  items.forEach((i) => {
    const key = (i.value || "").toLowerCase();
    valueCounts[key] = (valueCounts[key] || 0) + 1;
  });

  const autoSelectDuplicates = () => {
    const dupes = items.filter((i) => valueCounts[(i.value || "").toLowerCase()] > 1);
    setSelected(dupes.map((i) => i.id));
    setTargetId("");
  };

  const hasDuplicates = Object.values(valueCounts).some((c) => c > 1);

  const submit = async () => {
    if (!targetId) { toast.error("Select which option to keep"); return; }
    const sourceIds = selected.filter((id) => id !== targetId);
    if (sourceIds.length === 0) { toast.error("Select at least one option to merge into the target"); return; }

    const targetItem = items.find((i) => i.id === targetId);
    const sourceLabels = sourceIds.map((id) => items.find((i) => i.id === id)?.label || id).join(", ");

    if (!window.confirm(
      `Merge ${sourceIds.length} option(s) (${sourceLabels}) into "${targetItem?.label}"?\n\nAll leads using the merged values will be updated. This cannot be undone.`
    )) return;

    setSaving(true);
    try {
      const { data } = await api.post("/master-data/merge", { source_ids: sourceIds, target_id: targetId });
      toast.success(data.message);
      onClose();
      onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!merging} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-navy flex items-center gap-2">
            <Merge className="h-5 w-5" /> Merge {fieldLabel} Options
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <p className="text-sm text-muted-foreground">
            Select duplicate options, then choose which one to keep. All leads using the removed values will be updated to the kept value.
          </p>

          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${fieldLabel.toLowerCase()} options…`}
              className="pl-8"
              data-testid="merge-search-input"
            />
          </div>

          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={selectAll} className="text-xs">
              {allVisibleSelected ? (q ? "Deselect Matches" : "Deselect All") : (q ? "Select Matches" : "Select All")}
            </Button>
            {hasDuplicates && (
              <Button size="sm" variant="outline" onClick={autoSelectDuplicates} className="text-xs border-amber-300 text-amber-700 hover:bg-amber-50">
                Auto-select Duplicates
              </Button>
            )}
          </div>

          <div className="rounded-lg border border-navy/10 divide-y divide-navy/5 max-h-[40vh] overflow-y-auto">
            {visibleItems.length === 0 && (
              <p className="px-3 py-6 text-center text-sm text-muted-foreground">No options match "{query}"</p>
            )}
            {visibleItems.map((item) => {
              const isDupe = valueCounts[(item.value || "").toLowerCase()] > 1;
              const isSelected = selected.includes(item.id);
              const isTarget = targetId === item.id;
              return (
                <div
                  key={item.id}
                  className={`flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
                    isTarget ? "bg-green-50" : isSelected ? "bg-blue-50/50" : isDupe ? "bg-amber-50/40" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleSelect(item.id)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  <div className="flex-1 min-w-0">
                    <span className="font-medium text-ink">{item.label}</span>
                    <span className="ml-2 font-mono text-xs text-muted-foreground">({item.value})</span>
                    {isDupe && <span className="ml-2 text-[10px] font-semibold text-amber-600 bg-amber-100 rounded px-1.5 py-0.5">duplicate</span>}
                  </div>
                  {isSelected && (
                    <Button
                      size="sm"
                      variant={isTarget ? "default" : "outline"}
                      onClick={() => setTargetId(item.id)}
                      className={`shrink-0 text-xs h-7 ${isTarget ? "bg-green-600 text-white hover:bg-green-700" : "border-green-300 text-green-700 hover:bg-green-50"}`}
                    >
                      {isTarget ? "Keeping" : "Keep this"}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>

          {selected.length > 0 && !targetId && (
            <p className="text-xs text-amber-600 font-medium">Click "Keep this" on the option you want to keep.</p>
          )}

          {targetId && selected.length > 1 && (
            <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-800">
              <strong>{selected.length - 1}</strong> option(s) will be merged into <strong>"{items.find((i) => i.id === targetId)?.label}"</strong>. All leads using the removed values will be updated.
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={saving || !targetId || selected.length < 2}
            className="bg-navy text-white hover:bg-navy-hover"
            data-testid="merge-master-data-btn"
          >
            {saving ? "Merging…" : `Merge ${Math.max(0, selected.length - 1)} option(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
